import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "../../../AuthContext";
import URLS from "../../urls";
import LeftContainerFilters from "../Maps/Leak/components/LeftContainerFilters";
import filterConfig from "./components/filtersConfig";
import NotificationLegend from "./components/NotificationLegend";
import useNotificationStyles from "./components/NotificationStyles";
import Loading from "../components/Loading/Loading";

import {
  Typography,
  Box,
  IconButton,
  Paper,
  List,
  ListItemIcon,
  Drawer,
} from "@mui/material";

import NotificationsIcon from "@mui/icons-material/Notifications";
import TuneIcon from "@mui/icons-material/Tune";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import WaterDropIcon from "@mui/icons-material/WaterDrop";

function Notifications() {
  const { axiosInstance } = useAuth();
  const [notificationsData, setNotificationsData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const styles = useNotificationStyles();

  useEffect(() => {
    fetchNotificationData();
  }, []);

  const fetchNotificationData = async (filters = {}) => {
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams(filters);
      const url = `${URLS.NOTIFICATION.NOT_NOTIFIED_FIRST}?${searchParams.toString()}`;
      const response = await axiosInstance.get(url);
      setNotificationsData(response.data || []);
    } catch (error) {
      console.error("Notification error ", error);
      setNotificationsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = (filters, isClearing = false) => {
    if (isClearing) {
      fetchNotificationData();
    } else {
      fetchNotificationData(filters);
    }
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClick = async (notification) => {
    if (notification.type !== "SYSTEM") {
      if (notification.type === "LEAK") {
        const leakResponse = await axiosInstance.get(
          `${URLS.LEAK.MAIN}/${notification.leak}`
        );
        sessionStorage.setItem(
          "selectedLeak",
          JSON.stringify(leakResponse.data)
        );
      }
      navigate("/observations/" + notification.leak);
    }
  };

  const setNotified = async (notification_index) => {
    if (notificationsData[notification_index].notified) return;

    try {
      const notification = {
        ...notificationsData[notification_index],
        notified: new Date(),
      };
      await axiosInstance.put(
        URLS.NOTIFICATION.UPDATE + notification.id + "/",
        notification
      );
      setNotificationsData((prevData) => {
        const newData = [...prevData];
        newData[notification_index] = notification;
        return newData;
      });
    } catch (error) {
      console.error("Notification error", error);
    }
  };

  const typeIcon = (type) => {
    switch (type) {
      case "SYSTEM":
        return <WarningIcon color="error" />;
      case "OBSERVATION":
        return <CheckCircleIcon color="primary" />;
      case "LEAK":
        return <InfoIcon color="warning" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", width: "100%" }}>
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1000,
          }}
        >
          <Loading />
        </Box>
      )}
      <styles.NotificationContainer>
        <Paper elevation={3}>
          <styles.NotificationHeader>
            <styles.NotificationTitle variant="h5" component="h1">
              <NotificationsIcon
                sx={{ mr: 2, mb: 1, color: "#242A45" }}
                fontSize="large"
              />{" "}
              Notifications
            </styles.NotificationTitle>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ mr: 2, display: "inline-block" }}
              >
                Total: {notificationsData ? notificationsData.length : 0}
              </Typography>
              <IconButton onClick={toggleFilters} size="large" sx={{ mb: 0.8 }}>
                <TuneIcon />
              </IconButton>
            </Box>
          </styles.NotificationHeader>
        </Paper>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <styles.NotificationList>
            {notificationsData === null ? (
              <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
                Loading notifications...
              </Typography>
            ) : notificationsData.length === 0 ? (
              <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
                No notifications available
              </Typography>
            ) : (
              <List>
                {notificationsData.map((notification, index) => (
                  <styles.StyledListItem
                    key={notification.id}
                    notificationtype={notification.type.toLowerCase()}
                    notified={notification.notified}
                    onClick={() => handleClick(notification)}
                    onMouseEnter={() => setNotified(index)}
                    clickable={notification.type !== "SYSTEM"}
                  >
                    <styles.NotificationContent>
                      <Box
                        sx={{ display: "flex", flexDirection: "row", flex: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                          {typeIcon(notification.type)}
                        </ListItemIcon>
                        <Box>
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{ fontWeight: 550 }}
                          >
                            {String(
                              notification.type === "SYSTEM"
                                ? "SYSTEM ALERT"
                                : notification.type === "LEAK"
                                  ? `${notification.leak_info?.identifier || ""}`
                                  : notification.type === "OBSERVATION"
                                    ? `${notification.observation_info?.identifier || ""}`
                                    : `NEW ${notification.type}`
                            ).toUpperCase()}
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              mb: 1,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <styles.StyledChip
                              label={notification.type}
                              size="small"
                              notificationtype={notification.type.toLowerCase()}
                            />
                            {notification.type === "OBSERVATION" &&
                              notification.leak_info && (
                                <styles.StyledChip
                                  label={notification.leak_info.identifier}
                                  size="small"
                                  notificationtype="leak"
                                  icon={<WaterDropIcon fontSize="small" />}
                                />
                              )}
                          </Box>
                        </Box>
                      </Box>
                      <styles.NotificationMessage
                        variant="body2"
                        sx={{ flex: 1 }}
                      >
                        {notification.message &&
                        notification.message.trim() !== ""
                          ? notification.message
                          : "No info"}
                      </styles.NotificationMessage>
                      {notification.notified && (
                        <styles.NotificationDate variant="caption">
                          Notified:{" "}
                          {format(
                            new Date(notification.notified),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </styles.NotificationDate>
                      )}
                    </styles.NotificationContent>
                  </styles.StyledListItem>
                ))}
              </List>
            )}
          </styles.NotificationList>
          <NotificationLegend />
        </Box>
        <Drawer anchor="right" open={showFilters} onClose={toggleFilters}>
          <Box sx={{ width: 300, p: 2 }}>
            <LeftContainerFilters
              onApplyFilters={applyFilters}
              filterConfig={filterConfig}
              container={"notifications"}
            />
          </Box>
        </Drawer>
      </styles.NotificationContainer>
    </Box>
  );
}

export default Notifications;
